import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTransportAccess } from '@/hooks/use-transport-access';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, CheckCircle, MapPin, Users } from 'lucide-react';
import { US_STATES } from '@/constants/usStates';

interface TransporterForm {
  id?: string;
  name: string;
  email: string;
  phone: string;
  bio: string;
  species_handled: string[];
  vehicle_type: string;
  availability: string;
  address: string;
  city: string;
  state_code: string;
  service_radius_miles: number;
  is_active: boolean;
  is_verified: boolean;
}

const emptyForm: TransporterForm = {
  name: '',
  email: '',
  phone: '',
  bio: '',
  species_handled: [],
  vehicle_type: '',
  availability: '',
  address: '',
  city: '',
  state_code: 'SC',
  service_radius_miles: 50,
  is_active: true,
  is_verified: false,
};

export default function TransportDirectoryAdmin() {
  const queryClient = useQueryClient();
  const { isSiteAdmin, assignedStates, hasNationalAccess } = useTransportAccess();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [form, setForm] = useState<TransporterForm>(emptyForm);
  const [speciesInput, setSpeciesInput] = useState('');
  const [stateFilter, setStateFilter] = useState<string>(() => {
    // Default filter: 'all' for full access, first assigned state for restricted admins
    return 'all'; // Will be updated in useEffect when access is determined
  });

  // Check if user has full access (site admin or national transport admin)
  const hasFullAccess = isSiteAdmin || hasNationalAccess;

  // Set initial filter based on access level
  useEffect(() => {
    if (!hasFullAccess && assignedStates.length > 0) {
      setStateFilter(assignedStates[0]);
    }
  }, [hasFullAccess, assignedStates]);

  // Fetch transporters
  const { data: transporters = [], isLoading } = useQuery({
    queryKey: ['admin-transporters', stateFilter, hasFullAccess, assignedStates],
    queryFn: async () => {
      let query = supabase.from('transporters').select('*').order('created_at', { ascending: false });
      
      if (stateFilter !== 'all') {
        query = query.eq('state_code', stateFilter);
      } else if (!hasFullAccess && assignedStates.length > 0) {
        // State-specific transport admin: only show their assigned states
        query = query.in('state_code', assignedStates);
      }
      // If hasFullAccess is true and stateFilter is 'all', show all transporters
      
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
  });

  // Create/Update transporter
  const saveMutation = useMutation({
    mutationFn: async (data: TransporterForm) => {
      // Geocode the address
      const { data: geoData } = await supabase.functions.invoke('geocode-address', {
        body: { address: data.address, city: data.city, state: data.state_code }
      });

      const payload = {
        ...data,
        latitude: geoData?.latitude || null,
        longitude: geoData?.longitude || null,
      };

      if (data.id) {
        const { error } = await supabase
          .from('transporters')
          .update(payload)
          .eq('id', data.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('transporters')
          .insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-transporters'] });
      setIsDialogOpen(false);
      setForm(emptyForm);
      toast.success(form.id ? 'Transporter updated' : 'Transporter created');
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  // Delete transporter
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('transporters').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-transporters'] });
      toast.success('Transporter deleted');
    },
  });

  const handleEdit = (transporter: any) => {
    setForm({
      id: transporter.id,
      name: transporter.name,
      email: transporter.email || '',
      phone: transporter.phone || '',
      bio: transporter.bio || '',
      species_handled: transporter.species_handled || [],
      vehicle_type: transporter.vehicle_type || '',
      availability: transporter.availability || '',
      address: transporter.address,
      city: transporter.city,
      state_code: transporter.state_code,
      service_radius_miles: transporter.service_radius_miles || 50,
      is_active: transporter.is_active,
      is_verified: transporter.is_verified,
    });
    setIsDialogOpen(true);
  };

  const addSpecies = () => {
    if (speciesInput.trim() && !form.species_handled.includes(speciesInput.trim())) {
      setForm({ ...form, species_handled: [...form.species_handled, speciesInput.trim()] });
      setSpeciesInput('');
    }
  };

  const removeSpecies = (species: string) => {
    setForm({ ...form, species_handled: form.species_handled.filter(s => s !== species) });
  };

  // Available states: full access shows all, otherwise only assigned states
  const availableStates = hasFullAccess ? US_STATES : US_STATES.filter(s => assignedStates.includes(s.value));

  // Get default state for new transporters
  const getDefaultState = () => availableStates[0]?.value || 'SC';

  const handleAddNew = () => {
    setForm({ ...emptyForm, state_code: getDefaultState() });
    setIsDialogOpen(true);
  };

  const handleSave = () => {
    // Validate state access for non-full-access users
    if (!hasFullAccess && !availableStates.some(s => s.value === form.state_code)) {
      toast.error('You do not have permission to add transporters in this state');
      return;
    }
    saveMutation.mutate(form);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Transport Directory</h1>
          <p className="text-muted-foreground">Manage volunteer animal transporters</p>
        </div>
        <Button onClick={handleAddNew}>
          <Plus className="h-4 w-4 mr-2" />
          Add Transporter
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Total Transporters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{transporters.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Verified
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{transporters.filter(t => t.is_verified).length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Active
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{transporters.filter(t => t.is_active).length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex gap-4">
        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by state" />
          </SelectTrigger>
          <SelectContent>
            {hasFullAccess && <SelectItem value="all">All States</SelectItem>}
            {availableStates.map(state => (
              <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Species</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : transporters.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No transporters found
                </TableCell>
              </TableRow>
            ) : (
              transporters.map(transporter => (
                <TableRow key={transporter.id}>
                  <TableCell>
                    <div className="font-medium">{transporter.name}</div>
                    <div className="text-sm text-muted-foreground">{transporter.email}</div>
                  </TableCell>
                  <TableCell>
                    {transporter.city}, {transporter.state_code}
                    <div className="text-sm text-muted-foreground">
                      {transporter.service_radius_miles} mi radius
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {transporter.species_handled?.slice(0, 2).map((s: string) => (
                        <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                      ))}
                      {transporter.species_handled?.length > 2 && (
                        <Badge variant="outline" className="text-xs">+{transporter.species_handled.length - 2}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {transporter.is_active ? (
                        <Badge variant="secondary" className="bg-green-500/10 text-green-600">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                      {transporter.is_verified && (
                        <Badge variant="secondary" className="bg-blue-500/10 text-blue-600">Verified</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(transporter)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-destructive"
                        onClick={() => {
                          if (confirm('Delete this transporter?')) {
                            deleteMutation.mutate(transporter.id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit Transporter' : 'Add Transporter'}</DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Vehicle Type</Label>
                <Input 
                  placeholder="e.g., SUV, Van, Truck" 
                  value={form.vehicle_type} 
                  onChange={e => setForm({ ...form, vehicle_type: e.target.value })} 
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Bio</Label>
              <Textarea 
                placeholder="Brief description of experience and background..."
                value={form.bio} 
                onChange={e => setForm({ ...form, bio: e.target.value })} 
              />
            </div>

            <div className="space-y-2">
              <Label>Availability</Label>
              <Input 
                placeholder="e.g., Weekends, Evenings, Flexible" 
                value={form.availability} 
                onChange={e => setForm({ ...form, availability: e.target.value })} 
              />
            </div>

            <div className="space-y-2">
              <Label>Species Handled</Label>
              <div className="flex gap-2">
                <Input 
                  placeholder="Add species..."
                  value={speciesInput}
                  onChange={e => setSpeciesInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSpecies())}
                />
                <Button type="button" variant="secondary" onClick={addSpecies}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {form.species_handled.map(species => (
                  <Badge key={species} variant="secondary" className="cursor-pointer" onClick={() => removeSpecies(species)}>
                    {species} ×
                  </Badge>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Address *</Label>
                <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>City *</Label>
                <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>State *</Label>
                <Select value={form.state_code} onValueChange={v => setForm({ ...form, state_code: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableStates.map(state => (
                      <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Service Radius (miles)</Label>
              <Input 
                type="number" 
                min={1} 
                max={500}
                value={form.service_radius_miles} 
                onChange={e => setForm({ ...form, service_radius_miles: parseInt(e.target.value) || 50 })} 
              />
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={form.is_active} 
                  onCheckedChange={checked => setForm({ ...form, is_active: checked })} 
                />
                <Label>Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={form.is_verified} 
                  onCheckedChange={checked => setForm({ ...form, is_verified: checked })} 
                />
                <Label>Verified</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSave} 
              disabled={!form.name || !form.address || !form.city || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
